import { AxiosError } from 'axios';

/**
 * Интерфейс для ошибки API
 */
export interface ApiError {
  message: string;
  code?: string;
  details?: unknown;
}

/**
 * Получает user-friendly сообщение об ошибке из ответа API
 * @param error - Ошибка от axios или другого источника
 * @returns Понятное пользователю сообщение об ошибке
 * 
 * @example
 * ```ts
 * try {
 *   await api.sendRequest(data);
 * } catch (error) {
 *   const message = getErrorMessage(error);
 *   showNotification(message);
 * }
 * ```
 */
export const getErrorMessage = (error: unknown): string => {
  // Проверяем, является ли это AxiosError
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    
    // Слишком много запросов
    if (axiosError.response?.status === 429) {
      return 'Слишком много запросов. Попробуйте позже.';
    }
    
    // Ошибка валидации
    if (axiosError.response?.status === 400) {
      return axiosError.response.data?.message || 'Проверьте правильность заполнения полей.';
    }
    
    // Ошибка авторизации
    if (axiosError.response?.status === 401) {
      return 'Необходима авторизация.';
    }
    
    // Доступ запрещен
    if (axiosError.response?.status === 403) {
      return 'Доступ запрещен.';
    }
    
    // Не найдено
    if (axiosError.response?.status === 404) {
      return 'Запрашиваемый ресурс не найден.';
    }
    
    // Ошибка сервера
    if (axiosError.response?.status && axiosError.response.status >= 500) {
      return 'Произошла ошибка сервера. Попробуйте позже.';
    }
    
    // Ошибка сети
    if (axiosError.message === 'Network Error') {
      return 'Проблема с подключением. Проверьте интернет.';
    }
  }
  
  // Если это обычная ошибка с сообщением
  if (error instanceof Error) {
    return error.message;
  }
  
  // Общая ошибка
  return 'Произошла ошибка. Попробуйте позже.';
};

/**
 * Проверяет, является ли ошибка AxiosError
 * @param error - Ошибка для проверки
 * @returns true если это AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true;
}

/**
 * Логирует ошибку в консоль (только в development режиме)
 * @param error - Ошибка для логирования
 * @param context - Контекст, в котором произошла ошибка
 */
export const logError = (error: unknown, context?: string): void => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[Error${context ? ` in ${context}` : ''}]:`, error);
  }
};
